import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/movies';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/category/all?search=${encodeURIComponent(searchQuery.trim())}`);
      setIsMobileMenuOpen(false);
      setIsCategoryOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/50'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 flex items-center justify-center bg-accent-500 rounded-lg">
              <i className="ri-movie-2-line text-foreground-50 text-xl"></i>
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground-100 font-heading tracking-wide">
                8090影院
              </h1>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? 'text-primary-500 bg-primary-500/10'
                  : 'text-foreground-300 hover:text-foreground-100 hover:bg-background-100'
              }`}
            >
              首页
            </Link>

            <div className="relative" ref={categoryRef}>
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname.startsWith('/category')
                    ? 'text-primary-500 bg-primary-500/10'
                    : 'text-foreground-300 hover:text-foreground-100 hover:bg-background-100'
                }`}
              >
                电影分类
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isCategoryOpen && (
                <div className="absolute top-full mt-2 left-0 w-[560px] bg-background-100 border border-background-200/70 rounded-xl shadow-2xl p-4 grid grid-cols-3 gap-1 z-50">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-300 hover:text-primary-500 hover:bg-primary-500/10 transition-colors"
                    >
                      <div className="w-7 h-7 flex items-center justify-center">
                        <i className={`${cat.icon} text-base`}></i>
                      </div>
                      <span>{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-600">{cat.count}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/about'
                  ? 'text-primary-500 bg-primary-500/10'
                  : 'text-foreground-300 hover:text-foreground-100 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索电影..."
                className="w-56 pl-9 pr-4 py-2 bg-background-100 border border-background-200/70 rounded-full text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-foreground-500 text-sm"></i>
              </div>
            </form>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-200"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/50 py-4 space-y-1">
            <Link
              to="/"
              className="block px-4 py-3 rounded-lg text-sm text-foreground-300 hover:text-foreground-100 hover:bg-background-100 transition-colors"
            >
              首页
            </Link>
            {categories.slice(0, 8).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-2.5 px-4 py-3 rounded-lg text-sm text-foreground-300 hover:text-foreground-100 hover:bg-background-100 transition-colors"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className="block px-4 py-3 rounded-lg text-sm text-foreground-300 hover:text-foreground-100 hover:bg-background-100 transition-colors"
            >
              关于我们
            </Link>
            <div className="px-4 pt-2">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索电影..."
                  className="w-full pl-9 pr-4 py-2.5 bg-background-100 border border-background-200/70 rounded-full text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-foreground-500 text-sm"></i>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}